/**
 * CANONICAL ACCESS STATE — Single source of truth for platform-wide auth/access decisions.
 *
 * SYSTEM RULE: Loading/unresolved state is NEVER treated as denial.
 *
 * This hook composes all auth-related sub-states into a single normalized
 * decision object that every guard, route wrapper, header, and CTA must use.
 */

import { useMemo } from "react";
import { useAuth, AppRole } from "@/contexts/AuthContext";
import { useIsActivatedMember } from "@/hooks/useIsActivatedMember";
import { useProfile, Profile } from "@/hooks/useProfile";
import { useEntitlements, PlanTier } from "@/hooks/useEntitlements";
import { isProfileComplete, hasCompletedOnboarding } from "@/lib/auth/profile-completeness";

// ─── Canonical Access State ────────────────────────────────────────────────

export interface AccessState {
  // Sub-state resolution flags
  authLoading: boolean;
  authResolved: boolean;
  activationLoading: boolean;
  activationResolved: boolean;
  profileLoading: boolean;
  profileResolved: boolean;

  // Composite loading — true if ANY dimension is still resolving
  isLoading: boolean;

  /**
   * TRUE only when ALL resolution flags are true.
   * Guards, routes, and actions MUST NOT proceed until this is true.
   */
  isFullyResolved: boolean;

  /**
   * TRUE only when fully resolved AND authenticated.
   * Canonical flag for gating mutations, navigation triggers, and CMS actions.
   * Components MUST use this instead of local readiness patterns.
   */
  isActionSafe: boolean;

  // Identity
  isAuthenticated: boolean;
  user: ReturnType<typeof useAuth>["user"];
  role: AppRole;

  // Activation & profile
  isActivated: boolean;
  profile: Profile | null;
  hasCompletedProfile: boolean;
  hasCompletedOnboarding: boolean;

  // Membership / entitlements
  membershipLoading: boolean;
  membershipResolved: boolean;
  hasMembership: boolean;
  membershipTier: PlanTier;

  // Role helpers
  isAdmin: boolean;
  isAnalyst: boolean;
  canAccessAdmin: boolean;
  canAccessDashboard: boolean;
}

export function useAccessState(): AccessState {
  const { user, role, loading: authLoading } = useAuth();
  const { isActivated, isLoading: activationLoading } = useIsActivatedMember();
  const { data: profile, isLoading: profileQueryLoading } = useProfile();
  const { entitlements, status: entitlementStatus } = useEntitlements();

  // Profile query only runs when user exists; if no user, profile is resolved (as null)
  const profileLoading = !!user && profileQueryLoading;

  // Entitlements query only runs when user exists
  const membershipLoading = !!user && entitlementStatus === "loading";

  return useMemo(() => {
    const authResolved = !authLoading;
    const activationResolved = !activationLoading;
    const profileResolved = !profileLoading;
    const membershipResolved = !membershipLoading;

    const isAuthenticated = authResolved && !!user;
    const isLoading = !authResolved || !activationResolved || !profileResolved || !membershipResolved;
    const isFullyResolved = authResolved && activationResolved && profileResolved && membershipResolved;
    const isActionSafe = isFullyResolved && isAuthenticated;

    const profileComplete = isProfileComplete(profile);
    const onboardingComplete = hasCompletedOnboarding(profile);

    const tier = entitlements?.planTier ?? "free";
    const hasMembership = tier !== "free";

    const isAdmin = role === "admin" || role === "super_admin";
    const isAnalyst = role === "analyst";
    const canAccessAdmin = isAdmin || isAnalyst;

    // Dashboard requires authenticated + activated
    const canAccessDashboard = isAuthenticated && isActivated;

    if (import.meta.env.DEV && !isFullyResolved) {
      // Dev-only: trace which dimensions are still unresolved
      const pending = [
        !authResolved && "auth",
        !activationResolved && "activation",
        !profileResolved && "profile",
        !membershipResolved && "membership",
      ].filter(Boolean);
      if (pending.length > 0) {
        console.debug(`[AccessState] Still resolving: ${pending.join(", ")}`);
      }
    }

    return {
      authLoading,
      authResolved,
      activationLoading,
      activationResolved,
      profileLoading,
      profileResolved,
      isLoading,
      isFullyResolved,
      isActionSafe,
      isAuthenticated,
      user,
      role,
      isActivated,
      profile: profile ?? null,
      hasCompletedProfile: profileComplete,
      hasCompletedOnboarding: onboardingComplete,
      membershipLoading,
      membershipResolved,
      hasMembership,
      membershipTier: tier,
      isAdmin,
      isAnalyst,
      canAccessAdmin,
      canAccessDashboard,
    };
  }, [authLoading, user, role, activationLoading, isActivated, profileLoading, profile, membershipLoading, entitlements]);
}

// ─── Redirect Resolver ─────────────────────────────────────────────────────

/**
 * Centralized, deterministic redirect resolver.
 * Given resolved access state and gate requirements, returns the correct redirect target.
 * This is the ONLY place redirect destinations are defined.
 */
function resolveRedirect(
  state: AccessState,
  requirement: "auth" | "activation" | "profile" | "role",
): string {
  switch (requirement) {
    case "auth":
      return "/login";
    case "activation":
      return "/onboarding/profile";
    case "profile":
      return "/onboarding/profile";
    case "role":
      return "/unauthorized";
  }
}

// ─── Auth Gate Hook ────────────────────────────────────────────────────────

export interface AuthGateOptions {
  /** Require authenticated user. Default: true */
  requireAuth?: boolean;
  /** Require activated membership. Default: false */
  requireActivation?: boolean;
  /** Require profile.full_name to exist. Default: false */
  requireProfile?: boolean;
  /** Allowed roles. Empty = any role. Default: [] */
  allowedRoles?: AppRole[];
}

export interface AuthGateResult {
  /** True while any required state is still resolving */
  isLoading: boolean;
  /** True when user passes all gate requirements */
  isAllowed: boolean;
  /** True when state is resolved and user fails a requirement */
  isDenied: boolean;
  /** Suggested redirect path when denied, null if loading or allowed */
  redirectTo: string | null;
  /** Human-readable reason for denial (dev diagnostics) */
  reason: string | null;
  /** The full canonical access state */
  state: AccessState;
}

/**
 * Reusable guard hook. Returns a structured decision.
 * NEVER redirects internally — caller decides what to do.
 *
 * SYSTEM INVARIANT: No decision is made until isFullyResolved is true.
 * isDenied is NEVER true while isLoading is true.
 *
 * Evaluation order (canonical):
 *  0. ALL dimensions resolved?
 *  1. authenticated?
 *  2. activated?
 *  3. profile complete?
 *  4. role allowed?
 */
export function useAuthGate(options: AuthGateOptions = {}): AuthGateResult {
  const {
    requireAuth = true,
    requireActivation = false,
    requireProfile = false,
    allowedRoles = [],
  } = options;

  const state = useAccessState();

  return useMemo(() => {
    // ── Step 0: Full resolution gate ──
    // No route decision is permitted until ALL dimensions are resolved.
    if (!state.isFullyResolved) {
      if (import.meta.env.DEV && state.isLoading) {
        // Dev assertion: catch any caller that tries to act on partial state
      }
      return loading(state);
    }

    // ── Step 1: Auth ──
    if (requireAuth && !state.isAuthenticated) {
      return denied(state, resolveRedirect(state, "auth"), "User is not authenticated");
    }

    // ── Step 2: Activation ──
    if (requireActivation && !state.isActivated) {
      return denied(state, resolveRedirect(state, "activation"), "User is not activated");
    }

    // ── Step 3: Profile ──
    if (requireProfile && !state.hasCompletedProfile) {
      return denied(state, resolveRedirect(state, "profile"), "Profile incomplete (missing full_name)");
    }

    // ── Step 4: Role ──
    if (allowedRoles.length > 0) {
      const effectiveRoles = [...allowedRoles];
      if (effectiveRoles.includes("admin" as AppRole) && !effectiveRoles.includes("analyst" as AppRole)) {
        effectiveRoles.push("analyst" as AppRole);
      }
      if (!effectiveRoles.includes(state.role)) {
        return denied(state, resolveRedirect(state, "role"), `Role '${state.role}' not in allowed: ${effectiveRoles.join(",")}`);
      }
    }

    // All checks passed
    return {
      isLoading: false,
      isAllowed: true,
      isDenied: false,
      redirectTo: null,
      reason: null,
      state,
    };
  }, [state, requireAuth, requireActivation, requireProfile, allowedRoles]);
}

// ── Helpers ──

function loading(state: AccessState): AuthGateResult {
  return {
    isLoading: true,
    isAllowed: false,
    isDenied: false,
    redirectTo: null,
    reason: null,
    state,
  };
}

function denied(state: AccessState, redirectTo: string, reason: string): AuthGateResult {
  if (import.meta.env.DEV) {
    console.log(`[AuthGate] DENIED: ${reason}`);
  }
  return {
    isLoading: false,
    isAllowed: false,
    isDenied: true,
    redirectTo,
    reason,
    state,
  };
}
